import React, { useState, useCallback, useEffect } from "react";
import {
  fetchChatMessages,
  downloadChatMessagesCsv,
  fetchWorkflows,
  downloadWorkflowsCsv,
  fetchAnalysts,
  downloadAnalystsCsv,
  fetchMemory,
  downloadMemoryCsv,
  ChatMessageRow,
  WorkflowRow,
  AnalystRow,
  MemoryRow,
} from "services/UsageMetricsService";
import showToast from "hooks/useCustomToast";
import UsageMetricsPanel, { UsageType } from "modules/eva/components/UsageMetricsPanel";

function defaultStartDate(): Date {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d;
}

const UsageMetricsContainer: React.FC = () => {
  const [selectedType, setSelectedType] = useState<UsageType>("chats");

  // Picker state — updated on every date change but not yet applied
  const [startDate, setStartDate] = useState<Date | null>(defaultStartDate());
  const [endDate, setEndDate] = useState<Date | null>(new Date());

  // Applied state — only updated when the user clicks Apply; drives data fetching
  const [appliedStart, setAppliedStart] = useState<Date | null>(defaultStartDate());
  const [appliedEnd, setAppliedEnd] = useState<Date | null>(new Date());

  const [dateError, setDateError] = useState("");

  const [rows, setRows] = useState<ChatMessageRow[] | WorkflowRow[] | AnalystRow[] | MemoryRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const loadPage = useCallback(async (type: UsageType, start: Date, end: Date, pageOffset: number) => {
    setLoading(true);
    try {
      let result;
      if (type === "chats") {
        result = await fetchChatMessages(start, end, pageOffset);
      } else if (type === "workflows") {
        result = await fetchWorkflows(pageOffset);
      } else if (type === "analysts") {
        result = await fetchAnalysts(pageOffset);
      } else {
        result = await fetchMemory(pageOffset);
      }
      setRows(result.rows);
      setTotal(result.total);
      setOffset(result.offset);
      setHasMore(result.has_more);
    } catch (err) {
      showToast("Error loading usage metrics", err instanceof Error ? err.message : "Failed to load data.", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // Load on mount with defaults
  useEffect(() => {
    if (appliedStart && appliedEnd) {
      loadPage(selectedType, appliedStart, appliedEnd, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTypeChange = (type: UsageType) => {
    setSelectedType(type);
    setRows([]);
    setTotal(0);
    setOffset(0);
    setHasMore(false);
    if (type === "chats") {
      if (appliedStart && appliedEnd) {
        loadPage(type, appliedStart, appliedEnd, 0);
      }
    } else {
      loadPage(type, new Date(), new Date(), 0);
    }
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(date);
    if (date && endDate && date > endDate) {
      setDateError("Start date must be on or before end date.");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(date);
    if (startDate && date && startDate > date) {
      setDateError("Start date must be on or before end date.");
    } else {
      setDateError("");
    }
  };

  const handleApply = () => {
    if (!startDate || !endDate || dateError) return;
    setAppliedStart(startDate);
    setAppliedEnd(endDate);
    loadPage(selectedType, startDate, endDate, 0);
  };

  const handlePageChange = (newOffset: number) => {
    if (selectedType === "chats") {
      if (!appliedStart || !appliedEnd) return;
      loadPage(selectedType, appliedStart, appliedEnd, newOffset);
    } else {
      loadPage(selectedType, new Date(), new Date(), newOffset);
    }
  };

  const handleDownloadCsv = async () => {
    if (!appliedStart || !appliedEnd) return;
    setIsDownloading(true);
    const downloads = [
      () => downloadChatMessagesCsv(appliedStart, appliedEnd),
      () => downloadWorkflowsCsv(),
      () => downloadAnalystsCsv(),
      () => downloadMemoryCsv(),
    ];
    const errors: string[] = [];
    for (const download of downloads) {
      try {
        await download();
        // Small delay so the browser registers each anchor click separately.
        await new Promise(resolve => setTimeout(resolve, 300));
      } catch (err) {
        errors.push(err instanceof Error ? err.message : "Failed to download CSV.");
      }
    }
    setIsDownloading(false);
    if (errors.length > 0) {
      showToast("Some downloads failed", errors.join(" | "), "error");
    }
  };

  return (
    <UsageMetricsPanel
      selectedType={selectedType}
      onTypeChange={handleTypeChange}
      startDate={startDate}
      endDate={endDate}
      onStartDateChange={handleStartDateChange}
      onEndDateChange={handleEndDateChange}
      dateError={dateError}
      onApply={handleApply}
      rows={rows}
      total={total}
      offset={offset}
      hasMore={hasMore}
      loading={loading}
      onPageChange={handlePageChange}
      isDownloading={isDownloading}
      onDownloadCsv={handleDownloadCsv}
    />
  );
};

export default UsageMetricsContainer;
